/**
 * /settings → Quotas tab (admin-only). Per-user override op de
 * dagelijkse limiet voor `Vraag Claude` (`/api/v1/ai/transform`).
 *
 * Standaard-quota komt uit de worker (env-var of compiled fallback).
 * Een rij in deze lijst betekent: gebruiker X krijgt afwijkend limiet
 * Y in plaats van de default. Verwijderen ⇒ terug naar default.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../api/client";
import { ld } from "./ledgerUi";

interface Props {
  workspaceId: string;
}

interface OverrideRow {
  email: string;
  limit_kind: string;
  daily_limit: number;
  updated_at: string;
}

export function QuotasSettings({ workspaceId }: Props) {
  const { t } = useTranslation();
  const [defaults, setDefaults] = useState<{ ai_transform: number } | null>(null);
  const [overrides, setOverrides] = useState<OverrideRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Inline-toevoegen
  const [newEmail, setNewEmail] = useState("");
  const [newLimit, setNewLimit] = useState<number>(20);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await api.listAiRateLimits(workspaceId);
      setOverrides(res.overrides);
      setDefaults(res.defaults);
    } catch (err) {
      setError(explain(err));
    }
  }, [workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const upsert = async (email: string, limit: number | null) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await api.setAiRateLimit(workspaceId, email, {
        limit_kind: "ai_transform",
        daily_limit: limit,
      });
      setInfo(
        limit === null
          ? t("settings.quotas.toastReset", { email })
          : t("settings.quotas.toastSaved", { email, limit }),
      );
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const onAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !/^[^@\s]+@[^@\s]+$/.test(email)) {
      setError(t("settings.quotas.errorInvalidEmail"));
      return;
    }
    if (!Number.isInteger(newLimit) || newLimit < 0) {
      setError(t("settings.quotas.errorInvalidLimit"));
      return;
    }
    await upsert(email, newLimit);
    setNewEmail("");
    setNewLimit(20);
  };

  return (
    <div className="mt-1 flex flex-col gap-4">
      <p className={ld.sectionIntro}>
        {t("settings.quotas.defaultLimitBefore")}{" "}
        {defaults ? (
          <span className="font-medium text-(--ld-text)">
            {t("settings.quotas.defaultLimitDetail", { count: defaults.ai_transform })}
          </span>
        ) : (
          "—"
        )}
        {t("settings.quotas.defaultLimitAfter")}
      </p>

      {/* Inline-toevoegen */}
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void onAdd();
        }}
      >
        <div className="min-w-[14rem] flex-1">
          <label htmlFor="quota-email" className={ld.label}>
            {t("settings.quotas.userEmailLabel")}
          </label>
          <input
            id="quota-email"
            type="email"
            className={ld.input}
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder={t("settings.quotas.userEmailPlaceholder")}
          />
        </div>
        <div className="w-28">
          <label htmlFor="quota-limit" className={ld.label}>
            {t("settings.quotas.dailyLimitLabel")}
          </label>
          <input
            id="quota-limit"
            type="number"
            min={0}
            className={`${ld.input} text-right tabular-nums`}
            value={newLimit}
            onChange={(e) => setNewLimit(Number.parseInt(e.target.value, 10) || 0)}
          />
        </div>
        <button type="submit" className={ld.primary} disabled={busy}>
          {t("settings.quotas.addOverrideButton")}
        </button>
      </form>

      <div aria-live="polite">
        {error && (
          <p role="alert" className={ld.error}>
            {error}
          </p>
        )}
        {info && <p className={ld.success}>{info}</p>}
      </div>

      {overrides.length === 0 ? (
        <p className={`${ld.well} text-(--ld-muted) text-sm`}>{t("settings.quotas.emptyHint")}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-(--ld-border)">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-(--ld-border) border-b text-left text-(--ld-muted) text-xs">
                <th className="px-4 py-2 font-normal">{t("settings.quotas.colUser")}</th>
                <th className="px-4 py-2 font-normal">{t("settings.quotas.colKind")}</th>
                <th className="px-4 py-2 text-right font-normal">
                  {t("settings.quotas.colLimit")}
                </th>
                <th className="px-4 py-2 font-normal">{t("settings.quotas.colChanged")}</th>
                <th className="px-4 py-2">
                  <span className="sr-only">{t("settings.quotas.removeOverrideTitle")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {overrides.map((row) => (
                <RateRow
                  key={`${row.email}|${row.limit_kind}`}
                  row={row}
                  busy={busy}
                  onSave={(next) => void upsert(row.email, next)}
                  onRemove={() => void upsert(row.email, null)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RateRow({
  row,
  busy,
  onSave,
  onRemove,
}: {
  row: OverrideRow;
  busy: boolean;
  onSave: (limit: number) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<number>(row.daily_limit);
  return (
    <tr className="border-(--ld-rule) border-b last:border-b-0">
      <td className="px-4 py-2">{row.email}</td>
      <td className="px-4 py-2 text-(--ld-muted)">{row.limit_kind}</td>
      <td className="px-4 py-2 text-right">
        <input
          type="number"
          min={0}
          aria-label={t("settings.quotas.colLimit")}
          className={`${ld.inputInline} h-8 w-24 text-right tabular-nums`}
          value={draft}
          onChange={(e) => setDraft(Number.parseInt(e.target.value, 10) || 0)}
        />
      </td>
      <td className="whitespace-nowrap px-4 py-2 text-(--ld-muted) tabular-nums">
        {new Date(`${row.updated_at}Z`).toLocaleString()}
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center justify-end gap-3">
          {draft !== row.daily_limit && (
            <button
              type="button"
              className={ld.secondarySm}
              onClick={() => onSave(draft)}
              disabled={busy}
            >
              {t("settings.quotas.saveButton")}
            </button>
          )}
          <button
            type="button"
            className={ld.textDanger}
            onClick={onRemove}
            disabled={busy}
            title={t("settings.quotas.removeOverrideTitle")}
          >
            {t("settings.common.delete")}
          </button>
        </div>
      </td>
    </tr>
  );
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return "Unknown error";
}
